import { useState, useCallback, useMemo } from 'react'
import { SectionConfig, FieldMapping, PicklistOption } from '../types/metadata'
import {
  mockSections,
  mockFieldMappings,
  mockPicklistOptions,
} from '../data/mockData'
import { toast } from 'sonner'

export function useMetadataStore() {
  const [sections, setSections] = useState<SectionConfig[]>(mockSections)
  const [fieldMappings, setFieldMappings] =
    useState<FieldMapping[]>(mockFieldMappings)
  const [picklistOptions, setPicklistOptions] =
    useState<PicklistOption[]>(mockPicklistOptions)

  // Computed
  const getFieldCountForSection = useCallback(
    (sectionKey: string) => {
      return fieldMappings.filter((f) => f.section === sectionKey).length
    },
    [fieldMappings],
  )

  const getOptionCountForField = useCallback(
    (questionKey: string) => {
      return picklistOptions.filter((p) => p.questionKey === questionKey).length
    },
    [picklistOptions],
  )

  const orphanedFieldMappings = useMemo(() => {
    const sectionKeys = new Set(sections.map((s) => s.sectionKey))
    return fieldMappings.filter((f) => !sectionKeys.has(f.section))
  }, [sections, fieldMappings])

  // Section CRUD
  const addSection = useCallback(
    (section: SectionConfig) => {
      if (sections.some((s) => s.sectionKey === section.sectionKey)) {
        toast.error('Section Key must be unique')
        return false
      }
      setSections((prev) => [
        ...prev,
        { ...section, id: Math.random().toString(36).substring(7) },
      ])
      toast.success('Section created successfully')
      return true
    },
    [sections],
  )

  const updateSection = useCallback(
    (id: string, updates: Partial<SectionConfig>) => {
      if (
        updates.sectionKey &&
        sections.some((s) => s.sectionKey === updates.sectionKey && s.id !== id)
      ) {
        toast.error('Section Key must be unique')
        return false
      }

      // If sectionKey changed, update related field mappings
      const oldSection = sections.find((s) => s.id === id)
      if (
        oldSection &&
        updates.sectionKey &&
        oldSection.sectionKey !== updates.sectionKey
      ) {
        setFieldMappings((prev) =>
          prev.map((f) =>
            f.section === oldSection.sectionKey
              ? { ...f, section: updates.sectionKey! }
              : f,
          ),
        )
      }

      setSections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...updates } : s)),
      )
      toast.success('Section updated successfully')
      return true
    },
    [sections],
  )

  const deleteSection = useCallback(
    (id: string) => {
      const section = sections.find((s) => s.id === id)
      if (!section) return

      if (getFieldCountForSection(section.sectionKey) > 0) {
        toast.error('Cannot delete section with existing field mappings')
        return false
      }

      setSections((prev) => prev.filter((s) => s.id !== id))
      toast.success('Section deleted successfully')
      return true
    },
    [sections, getFieldCountForSection],
  )

  const reorderSection = useCallback((id: string, direction: 'up' | 'down') => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id)
      if (idx < 0) return prev
      if (direction === 'up' && idx === 0) return prev
      if (direction === 'down' && idx === prev.length - 1) return prev

      const newSections = [...prev]
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1

      // Swap orders
      const tempOrder = newSections[idx].order
      newSections[idx].order = newSections[targetIdx].order
      newSections[targetIdx].order = tempOrder

      // Swap positions in array for immediate visual feedback
      const temp = newSections[idx]
      newSections[idx] = newSections[targetIdx]
      newSections[targetIdx] = temp

      return newSections
    })
  }, [])

  // Field Mapping CRUD
  const addFieldMapping = useCallback(
    (field: FieldMapping) => {
      if (fieldMappings.some((f) => f.questionKey === field.questionKey)) {
        toast.error('Question Key must be unique')
        return false
      }
      setFieldMappings((prev) => [
        ...prev,
        { ...field, id: Math.random().toString(36).substring(7) },
      ])
      toast.success('Field mapping created successfully')
      return true
    },
    [fieldMappings],
  )

  const updateFieldMapping = useCallback(
    (id: string, updates: Partial<FieldMapping>) => {
      if (
        updates.questionKey &&
        fieldMappings.some(
          (f) => f.questionKey === updates.questionKey && f.id !== id,
        )
      ) {
        toast.error('Question Key must be unique')
        return false
      }

      // If questionKey changed, update related picklist options
      const oldField = fieldMappings.find((f) => f.id === id)
      if (
        oldField &&
        updates.questionKey &&
        oldField.questionKey !== updates.questionKey
      ) {
        setPicklistOptions((prev) =>
          prev.map((p) =>
            p.questionKey === oldField.questionKey
              ? { ...p, questionKey: updates.questionKey! }
              : p,
          ),
        )
      }

      setFieldMappings((prev) =>
        prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
      )
      toast.success('Field mapping updated successfully')
      return true
    },
    [fieldMappings],
  )

  const deleteFieldMapping = useCallback(
    (id: string) => {
      const field = fieldMappings.find((f) => f.id === id)
      if (!field) return

      if (getOptionCountForField(field.questionKey) > 0) {
        toast.error(
          'Cannot delete field mapping with existing picklist options',
        )
        return false
      }

      setFieldMappings((prev) => prev.filter((f) => f.id !== id))
      toast.success('Field mapping deleted successfully')
      return true
    },
    [fieldMappings, getOptionCountForField],
  )

  const reorderFieldMapping = useCallback(
    (id: string, direction: 'up' | 'down') => {
      setFieldMappings((prev) => {
        const idx = prev.findIndex((f) => f.id === id)
        if (idx < 0) return prev

        const field = prev[idx]
        // Only reorder within the same section
        const sectionFields = prev
          .filter((f) => f.section === field.section)
          .sort((a, b) => a.positionInSection - b.positionInSection)
        const sectionIdx = sectionFields.findIndex((f) => f.id === id)

        if (direction === 'up' && sectionIdx === 0) return prev
        if (direction === 'down' && sectionIdx === sectionFields.length - 1)
          return prev

        const targetSectionIdx =
          direction === 'up' ? sectionIdx - 1 : sectionIdx + 1
        const targetField = sectionFields[targetSectionIdx]

        const newMappings = [...prev]
        const globalIdx1 = newMappings.findIndex((f) => f.id === field.id)
        const globalIdx2 = newMappings.findIndex((f) => f.id === targetField.id)

        const tempPos = newMappings[globalIdx1].positionInSection
        newMappings[globalIdx1].positionInSection =
          newMappings[globalIdx2].positionInSection
        newMappings[globalIdx2].positionInSection = tempPos

        return newMappings
      })
    },
    [],
  )

  // Picklist Option CRUD
  const addPicklistOption = useCallback((option: PicklistOption) => {
    setPicklistOptions((prev) => [
      ...prev,
      { ...option, id: Math.random().toString(36).substring(7) },
    ])
    toast.success('Picklist option added successfully')
    return true
  }, [])

  const addBulkPicklistOptions = useCallback((options: PicklistOption[]) => {
    const newOptions = options.map((o) => ({
      ...o,
      id: Math.random().toString(36).substring(7),
    }))
    setPicklistOptions((prev) => [...prev, ...newOptions])
    toast.success(`${options.length} options added successfully`)
    return true
  }, [])

  const updatePicklistOption = useCallback(
    (id: string, updates: Partial<PicklistOption>) => {
      setPicklistOptions((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...updates } : p)),
      )
      toast.success('Picklist option updated successfully')
      return true
    },
    [],
  )

  const deletePicklistOption = useCallback((id: string) => {
    setPicklistOptions((prev) => prev.filter((p) => p.id !== id))
    toast.success('Picklist option deleted successfully')
    return true
  }, [])

  const reorderPicklistOption = useCallback(
    (id: string, direction: 'up' | 'down') => {
      setPicklistOptions((prev) => {
        const idx = prev.findIndex((p) => p.id === id)
        if (idx < 0) return prev

        const option = prev[idx]
        const fieldOptions = prev
          .filter((p) => p.questionKey === option.questionKey)
          .sort((a, b) => a.order - b.order)
        const fieldIdx = fieldOptions.findIndex((p) => p.id === id)

        if (direction === 'up' && fieldIdx === 0) return prev
        if (direction === 'down' && fieldIdx === fieldOptions.length - 1)
          return prev

        const targetFieldIdx = direction === 'up' ? fieldIdx - 1 : fieldIdx + 1
        const targetOption = fieldOptions[targetFieldIdx]

        const newOptions = [...prev]
        const globalIdx1 = newOptions.findIndex((p) => p.id === option.id)
        const globalIdx2 = newOptions.findIndex((p) => p.id === targetOption.id)

        const tempOrder = newOptions[globalIdx1].order
        newOptions[globalIdx1].order = newOptions[globalIdx2].order
        newOptions[globalIdx2].order = tempOrder

        return newOptions
      })
    },
    [],
  )

  return {
    sections,
    fieldMappings,
    picklistOptions,
    orphanedFieldMappings,
    getFieldCountForSection,
    getOptionCountForField,
    addSection,
    updateSection,
    deleteSection,
    reorderSection,
    addFieldMapping,
    updateFieldMapping,
    deleteFieldMapping,
    reorderFieldMapping,
    addPicklistOption,
    addBulkPicklistOptions,
    updatePicklistOption,
    deletePicklistOption,
    reorderPicklistOption,
  }
}
